import { useEffect, useRef, useState } from "react";
import YouTube from "react-youtube";
import Player from "@vimeo/player";
import "./../styles/video-card-previewer.scss";

// https://developers.google.com/youtube/player_parameters
const YT_PLAYER_OPTIONS = {
    width: '100%',
    height: '100%',
    playerVars: {
        autoplay: 1,
        rel: 0,
        controls: 0,
        modestbranding: 0,
        showinfo: 0
    }
};
const MAX_PLAYING_TIME = 120; // Seconds

const VideoPreviewer = ({
    thumbnail,
    videoType,
    embedId,
    locked
}) => {
    const containerRef = useRef(null);
    const [video, setVideo] = useState(null);
    const [hovered, setHovered] = useState(false);
    const [showBanner, setShowBanner] = useState(false);


    /** * Subscribe Banner Component
     * @returns {JSX.Element} The Subscribe Banner component.
     */
    const SubscribeBanner = () => {
        return (
            <div className="subscribe-banner">
                <div className="subscribe-banner-text text-center">
                    <h3>Enjoyed the preview? </h3>
                    <p>Unlock the full video to continue watching beyond the 2-minute mark.</p>
                </div>
            </div>
        );
    };

    /**
     * @param {YouTube Player Previewer} param0 
     * @returns 
     */
    const YoutubePreviewer = ({ afterPreviewEnd }) => {
        const playerRef = useRef(null);
        const intervalRef = useRef(null);

        const endVideo = () => {
            clearInterval(intervalRef.current);
            afterPreviewEnd();
        };

        const onReady = (e) => {
            playerRef.current = e.target;
            playerRef.current.playVideo();

            // Start tracking time
            intervalRef.current = setInterval(async () => {
                const currentTime = await playerRef.current.getCurrentTime();
                if (currentTime >= MAX_PLAYING_TIME) {
                    afterPreviewEnd();
                    playerRef.current.pauseVideo();
                    clearInterval(intervalRef.current);
                }
            }, 1000);

        };

        useEffect(() => {
            return () => {
                clearInterval(intervalRef.current);
            };
        }, []);

        return (
            <div className="youtube-video-wrapper">
                <YouTube
                    videoId={video.embedId}
                    opts={YT_PLAYER_OPTIONS}
                    onReady={onReady}
                    onEnd={endVideo}
                />
            </div>
        );
    };

    /**
     * @param {Vimeo Player Previewer} param0 
     * @returns 
     */
    const VimeoPreviewer = ({ afterPreviewEnd }) => {
        const vimeoContainerRef = useRef(null);
        const [vimeoPlayer, setVimeoPlayer] = useState(null);

        const destroyPlayer = () => {
            vimeoPlayer?.off("play");
            setVimeoPlayer(null);
        };

        const endVideo = () => {
            vimeoPlayer.pause();
            destroyPlayer();
            afterPreviewEnd();
        };

        const onVideoEnded = () => {
            endVideo();
        };

        const onPlayerTimeUpdate = (event) => {
            const seconds = event.seconds;
            if (seconds >= MAX_PLAYING_TIME) {
                afterPreviewEnd();
                vimeoPlayer.unload();
            }
        }

        const handleVimeoOnPlay = () => { };

        useEffect(() => {
            if (vimeoContainerRef?.current && !vimeoPlayer) {
                const player = new Player(vimeoContainerRef?.current, {
                    id: video.embedId,
                    width: "100%",
                    height: "100%",
                    autoplay: true,
                    controls: false
                });
                setVimeoPlayer(player);
            }
        }, [vimeoContainerRef]);

        useEffect(() => {
            if (vimeoPlayer) {
                vimeoPlayer.on("play", handleVimeoOnPlay);
                vimeoPlayer.on("ended", onVideoEnded);
                vimeoPlayer.on('timeupdate', onPlayerTimeUpdate);
                vimeoPlayer.on("error", () => {
                    destroyPlayer();
                });
                return () => {
                    destroyPlayer();
                };
            }
        }, [vimeoPlayer]);

        return (
            <>
                <div className="vimeo-video-wrapper">
                    <div ref={vimeoContainerRef}></div>
                </div>
            </>
        );
    };

    const onMouseEnter = () => {
        setHovered(true);
        setShowBanner(false);
    }

    const onMouseLeave = () => {
        setHovered(false);
        setShowBanner(false);
    }

    const onPreviewEnd = () => {
        setHovered(false);
        setShowBanner(true);
    };

    useEffect(() => {
        if (hovered && videoType && embedId) {
            setVideo({
                videoType: videoType,
                embedId: embedId
            });
        } else {
            setVideo(null);
        }
        return () => {
            setVideo(null);
        };
    }, [hovered, videoType, embedId]);

    return (
        <>
            <div className="video-card-previewer-wrapper">
                <div
                    ref={containerRef}
                    className="video-card-previewer"
                    onMouseEnter={onMouseEnter}
                    onFocus={onMouseEnter}
                    onMouseLeave={onMouseLeave}
                    onBlur={onMouseLeave}
                    onClick={onMouseLeave}
                >
                    <div
                        style={{ opacity: video && hovered ? 0 : 1 }}
                        className={"video-card-previewer-thumbnail"}>
                        {thumbnail}
                        {locked && showBanner && (
                            <SubscribeBanner />
                        )}
                    </div>

                    <div
                        className="video-card-previewer-video"
                        style={{ opacity: video && hovered ? 1 : 0 }}>
                        {(video && hovered) && (
                            <>
                                {video?.videoType === "youtube" && (
                                    <YoutubePreviewer
                                        afterPreviewEnd={onPreviewEnd}
                                    />
                                )}
                                {video?.videoType === "vimeo" && (
                                    <VimeoPreviewer
                                        afterPreviewEnd={onPreviewEnd}
                                    />
                                )}
                            </>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
};

export default VideoPreviewer;
